import { createInstance, i18n } from 'i18next';
import HttpBackend from 'i18next-http-backend';
import capitalize from 'lodash.capitalize';
import { FC, PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import { useApplicationConfig } from '@bonusly/application';
import { joinPath } from '@bonusly/url';

const namespaces = [
	'action_input',
	'admin_dashboard',
	'admin_rewards',
	'awards',
	'announcements',
	'bookmarks',
	'celebrations',
	'common',
	'components',
	'feedback',
	'growth',
	'invite_users',
	'manager_enablement',
	'monarch',
	'navigation',
	'nominations',
	'onboarding',
	'recognition',
	'rewards',
	'vertical_navbar',
];

type CurrencyFormat = {
	plural: string;
	singular: string;
};

const budget = (value: number, lng: string | undefined, options: CurrencyFormat) => {
	const formattedNumber = new Intl.NumberFormat(lng).format(value);
	const string = value === 1 ? options.singular : options.plural;

	return string.replace('[X]', formattedNumber);
};

const dateRange = (value: { start: Date; end: Date }, lng: string | undefined, options: Intl.DateTimeFormatOptions) => {
	return new Intl.DateTimeFormat(lng, options ?? { year: '2-digit', month: 'numeric', day: 'numeric' }).formatRange(value.start, value.end);
};

const padZero = (value: number) => value.toString().padStart(2, '0');

const initClient = (origin: string) => {
	const loadPath = joinPath(origin, '/{{lng}}/{{ns}}.json');
	const i18n = createInstance();

	i18n.use(HttpBackend).use(initReactI18next).init({
		backend: {
			loadPath,
		},
		fallbackLng: 'en',
		maxRetries: 1,
		returnNull: false,
		ns: namespaces,
	});

	i18n.services.formatter?.add('budget', budget);
	i18n.services.formatter?.add('capitalize', capitalize);
	i18n.services.formatter?.add('daterange', dateRange);
	i18n.services.formatter?.add('padzero', padZero);

	return i18n;
};

export const TranslationProvider: FC<PropsWithChildren> = ({ children }) => {
	const origin = useApplicationConfig('translationsOrigin');
	const [client, setClient] = useState<i18n | null>();

	useEffect(() => {
		if (!origin || client) return;
		setClient(initClient(origin));
	}, [origin]);

	if (!client) return null;

	return (
		<Suspense>
			<I18nextProvider i18n={client}>{children}</I18nextProvider>
		</Suspense>
	);
};
